'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, TrendingUp, TrendingDown, Wind } from 'lucide-react';
import type { RegimeInfo } from '@/types/markov.types';

interface RegimeIndicatorProps {
  regime: RegimeInfo;
  className?: string;
}

const regimeConfig: Record<string, {
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof Activity;
  description: string;
}> = {
  'trending': {
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: TrendingUp,
    description: 'Strong directional movement with clear trend',
  },
  'ranging': {
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: Activity,
    description: 'Price moving within a defined range',
  },
  'volatile': {
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: Wind,
    description: 'High volatility with unpredictable price swings',
  },
  'mean_reverting': {
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    icon: TrendingDown,
    description: 'Price tends to return to average levels',
  },
  'breakout': {
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: TrendingUp,
    description: 'Breaking through key support/resistance levels',
  },
  'consolidation': {
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    icon: Activity,
    description: 'Price consolidating before next move',
  },
};

export function RegimeIndicator({ regime, className }: RegimeIndicatorProps) {
  const regimeName = regime.regime.toLowerCase();
  const config = regimeConfig[regimeName] || regimeConfig['ranging'];
  const Icon = config.icon;

  return (
    <Card className={`${config.borderColor} border-2 ${className}`}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-full ${config.bgColor}`}>
            <Icon className={`w-6 h-6 ${config.color}`} />
          </div>
          <div>
            <CardTitle className={config.color}>
              {regime.regime}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {config.description}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Confidence Meter */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium">Regime Confidence</span>
            <span className={`font-bold ${config.color}`}>
              {(regime.confidence * 100).toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${config.bgColor} border-2 ${config.borderColor}`}
              style={{ width: `${regime.confidence * 100}%` }}
            />
          </div>
        </div>

        {/* Characteristics */}
        {regime.characteristics && regime.characteristics.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-2">Market Characteristics</div>
            <div className="flex flex-wrap gap-2">
              {regime.characteristics.map((char, i) => (
                <span
                  key={i}
                  className={`px-3 py-1 ${config.bgColor} ${config.color} rounded-md text-sm font-medium border ${config.borderColor}`}
                >
                  {char}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Confidence Level Indicator */}
        <div className={`p-3 rounded-md ${config.bgColor} border ${config.borderColor}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Signal Reliability</span>
            <span className={`text-sm font-bold ${config.color}`}>
              {regime.confidence > 0.7 ? 'HIGH' : regime.confidence > 0.4 ? 'MEDIUM' : 'LOW'}
            </span>
          </div>
          {regime.confidence < 0.4 && (
            <p className="text-xs mt-2 text-muted-foreground">
              Low confidence - consider waiting for clearer regime signal
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Timeline component for regime history
interface RegimeTimelineProps {
  regimes: {
    timestamp: string;
    regime: RegimeInfo;
  }[];
  className?: string;
}

export function RegimeTimeline({ regimes, className }: RegimeTimelineProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Regime History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {regimes.map((entry, i) => {
            const regimeName = entry.regime.regime.toLowerCase();
            const config = regimeConfig[regimeName] || regimeConfig['ranging'];

            return (
              <div
                key={i}
                className={`flex items-center gap-4 p-3 rounded-md border ${config.borderColor} ${config.bgColor}`}
              >
                <div className="flex-shrink-0 text-xs text-muted-foreground w-24">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </div>
                <div className="flex-1">
                  <div className={`font-semibold ${config.color}`}>
                    {entry.regime.regime}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Confidence: {(entry.regime.confidence * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-12 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${config.bgColor} border ${config.borderColor}`}
                      style={{ width: `${entry.regime.confidence * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
